import styled, {
  SystemProp,
  Theme,
  ThemeSize,
  x
} from '@xstyled/styled-components';
import * as CSS from 'csstype';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flex } from '~/ui/flex/Flex';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { IMAGE, LAYOUT_LIST, LAYOUT_COLUMNS } from '../itemIds';
import phoneImage from '~/assets/1x1.png';
import { useEditorStore } from '~/shell/Store';
import { EditBg } from '~/editor/buttons/EditBg';

interface Props {
  url: string;
  alt?: string;
  darken?: boolean;
  className?: string;
  borderRadius?: string;
  backgroundSizeForMobile?: string;
  width?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  height?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  ariaHidden?: boolean;
  objectFit?: CSS.Property.ObjectFit;
  divWidth?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  divHeight?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  editButtonOneTheImage?: boolean;
  role?: string;
}

const DivEl = styled.div<Props>`
  background-image: url(${p => p.url});
  background-position: center;
  background-size: cover;
  flex: 1;
  width: 100%;
  height: ${p => p.height || '100%'};
  filter: brightness(${p => (p.darken ? '0.4' : '1')});
  position: relative;
  background-repeat: no-repeat;
  @media (max-width: sm) {
    background-size: ${p => p.backgroundSizeForMobile || 'cover'};
  }
`;

const ImageEl = styled.imgBox<Partial<Props>>`
  object-fit: contain;
  width: 100%;
  filter: brightness(${p => (p.darken ? '0.4' : '1')});
  border-radius: ${p => p.borderRadius || '0px'};
  pointer-events: none;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export type ImageDefinition = ItemDefinition<typeof IMAGE, Props>;

export class Image extends Item<typeof IMAGE, Props> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected render({
    parent,
    width,
    height,
    editButtonOneTheImage,
    role = 'img',
    ...props
  }: RenderProps<Props>) {
    const { contentEditable } = useEditorStore();
    const divElRef = useRef<HTMLDivElement>(null);
    const imgElRef = useRef<HTMLImageElement>(null);
    const [showPortal, setShowPortal] = useState(false);

    React.useEffect(() => {
      if (divElRef.current) {
        setShowPortal(true);
      }
    }, [divElRef.current]);

    const getParentId = () => {
      const parentId = parent?.id;

      if (parentId) {
        const parentIdParts = parentId.split('.');
        if (parentIdParts.length > 1) {
          const firstPart = parentIdParts[0];
          const numValue = Number(firstPart);
          if (!isNaN(numValue) && numValue > 100) {
            return firstPart as string;
          }
        }
      }
      return undefined;
    };

    // The image is rendered differently based on the parent type...
    switch (parent?.type) {
      case LAYOUT_COLUMNS:
      case LAYOUT_LIST:
        return (
          <x.div
            position="relative"
            ref={divElRef}
            w={props.divWidth}
            h={props.divHeight}
          >
            <ImageEl
              src={props.url}
              alt={props.alt || ''}
              darken={props.darken}
              className={props.className}
              borderRadius={props.borderRadius}
              aria-hidden={props.ariaHidden}
              w={width}
              h={height}
              objectFit={props.objectFit}
            />
            {contentEditable && (
              <EditBg
                divElRef={divElRef}
                id={props.id}
                type="image"
                parentId={getParentId()}
              />
            )}
            <Flex variant="center" position="absolute" top="0" left="0">
              {props.children}
            </Flex>
          </x.div>
        );
      default:
        /*
          WGAC - There is no need to provide a text alternative in a aria-label attribute if you can provide it in a alt attribute instead.
        */
        return (
          <>
            <DivEl
              {...props}
              height={height}
              role={role}
              alt=""
              ref={divElRef}
            />
            {contentEditable && editButtonOneTheImage ? (
              <ImageWrapper>
                <x.img
                  ref={imgElRef}
                  src={phoneImage}
                  w="1px"
                  h="1px"
                  position="absolute"
                  alt={props.alt || ''}
                  aria-hidden={props.ariaHidden}
                  className="bg-wrapper"
                />
                <EditBg
                  divElRef={imgElRef as React.RefObject<HTMLDivElement>}
                  id={props.id}
                  type="image"
                  style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 199
                  }}
                />
              </ImageWrapper>
            ) : (
              <x.img
                src={phoneImage}
                w="1px"
                h="1px"
                position="absolute"
                alt={props.alt || ''}
                aria-hidden={props.ariaHidden}
                className="bg-wrapper"
              />
            )}

            {contentEditable &&
              !editButtonOneTheImage &&
              showPortal &&
              createPortal(
                <EditBg divElRef={divElRef} id={props.id} type="image" />,
                document.body
              )}
          </>
        );
    }
  }
}

registry[IMAGE] = Image;
